import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import React from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import zoomPlugin from 'chartjs-plugin-zoom'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid'
import { ChartBarIcon, FireIcon, HeartIcon, ScaleIcon } from '@heroicons/react/24/outline'
import { supabase } from './supabaseClient'
import { BoltIcon } from '@heroicons/react/24/outline'
import WeightGame from './components/WeightGame'
import { useNavigate } from 'react-router-dom'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
)

interface WeightEntry {
  date: string
  weight: number
  active_energy?: number
  body_fat_percentage?: number
  body_mass_index?: number
  cycling_distance?: number
  weekly_energy?: number
}

type TimeRange = '1w' | '2w' | '1m' | '1y' | 'all'

// Add sound files array
const CLICK_SOUNDS = [
  '/sounds/click1.mp3',
  '/sounds/click2.mp3',
  '/sounds/click3.mp3',
  '/sounds/click4.mp3',
  '/sounds/click5.mp3',
  '/sounds/click6.mp3',
  '/sounds/click7.mp3',
  '/sounds/click8.mp3',
  '/sounds/click9.mp3'
]

type ThemeType = 'modern' | 'default' | 'ocean' | 'sunset' | 'forest'

type ThemeConfig = {
  primary: string
  secondary: string
  accent: string
  button: string
  buttonSecondary: string
  card: string
  text: string
  textSecondary: string
  glow: string
  toggle: string
  chart: {
    primary: string
    background: string
    grid: string
  }
}

function shuffleArray<T>(array: T[]): T[] {
  const newArray = [...array]
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]]
  }
  return newArray
}

interface MetricCardProps {
  title: string
  value: number | null
  unit: string
  icon: React.ComponentType<any> | (() => React.ReactNode)
  change?: number | null
  invertColors?: boolean
  onPlaySound: () => void
  onClick?: () => void
  theme: ThemeConfig
  customContent?: React.ReactNode
}

function MetricCard({ 
  title, 
  value, 
  unit, 
  icon: Icon,
  change,
  invertColors = false,
  onPlaySound,
  onClick,
  theme,
  customContent
}: MetricCardProps) {
  const isPositiveChange = change !== null && change !== undefined ? 
    (invertColors ? change < 0 : change >= 0) : 
    false

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onPlaySound()
    if (onClick) onClick()
  }

  const formatValue = (val: number | null) => {
    if (val === null) return '-'
    if (unit === 'kcal') {
      return Math.round(val).toLocaleString('en-US').replace(/,/g, '.')
    }
    return val.toFixed(1)
  }
  
  return (
    <motion.div 
      className={`stat-card cursor-pointer ${theme.card} ${theme.glow}`}
      onClick={handleClick}
      whileHover={onClick ? { scale: 1.02 } : undefined}
      whileTap={onClick ? { scale: 0.98 } : undefined}
    >
      <div className="flex items-center justify-between">
        <div className={`flex items-center gap-2 ${theme.textSecondary}`}>
          {typeof Icon === 'function' && React.isValidElement(<Icon />) ? (
            <Icon className="h-5 w-5" />
          ) : (
            typeof Icon === 'function' ? <Icon /> : null
          )}
          <span>{title}</span>
        </div>
        {change !== undefined && change !== null && (
          <div className={`flex items-center gap-1 text-sm ${isPositiveChange ? 'text-emerald-400' : 'text-rose-400'}`}>
            {change >= 0 ? <ArrowUpIcon className="h-4 w-4" /> : <ArrowDownIcon className="h-4 w-4" />}
            <span>{Math.abs(unit === 'kcal' ? Math.round(change) : Number(change.toFixed(1)))}</span>
          </div>
        )}
      </div>
      {customContent ? (
        customContent
      ) : (
        <div className={`mt-2 text-2xl font-bold ${theme.text}`}>
          {formatValue(value)}{unit ? ` ${unit}` : ''}
        </div>
      )}
    </motion.div>
  )
}

// Add new type for energy time period
type EnergyTimePeriod = 'weekly' | 'monthly' | 'yearly'

function App() {
  const [currentWeight, setCurrentWeight] = useState<number | null>(null)
  const [previousWeight, setPreviousWeight] = useState<number | null>(null)
  const [weightHistory, setWeightHistory] = useState<WeightEntry[]>([])
  const [showDetailedGraph, setShowDetailedGraph] = useState(false)
  const [timeRange, setTimeRange] = useState<TimeRange>('2w')
  const [displayCount, setDisplayCount] = useState(1000)
  const [isLoaded, setIsLoaded] = useState(false)
  const [currentMetrics, setCurrentMetrics] = useState<Partial<WeightEntry>>({})
  const [previousMetrics, setPreviousMetrics] = useState<Partial<WeightEntry>>({})
  const [activeMetric, setActiveMetric] = useState<'weight' | 'energy' | 'cycling'>('weight')
  const [averageMode, setAverageMode] = useState<'raw' | 'weekly' | 'monthly'>('raw')
  const [yesterdayMetrics, setYesterdayMetrics] = useState<Partial<WeightEntry>>({})
  const [soundQueue, setSoundQueue] = useState<string[]>([])
  const [clickEffects, setClickEffects] = useState<Array<{ id: number; x: number; y: number }>>([])
  const [showGame, setShowGame] = useState(false)
  const [showOptions, setShowOptions] = useState(false)
  const [options, setOptions] = useState({
    enableSounds: false,
    enableAnimations: true,
    enableClickEffects: true,
    darkMode: true,
    theme: 'modern' as ThemeType,
    showPoopCard: false
  })

  // Add state for last poop
  const [lastPoop, setLastPoop] = useState<string | null>(null)

  // Add new state for energy time period
  const [energyTimePeriod, setEnergyTimePeriod] = useState<EnergyTimePeriod>('weekly')

  const headingText = "Gutzeit Weight Tracker"
  const words = headingText.split(" ")
  const letterAnimationVariants = {
    initial: { 
      opacity: 0,
      y: 20,
      rotate: -10,
      scale: 0.5
    },
    animate: (i: number) => ({
      opacity: 1,
      y: 0,
      rotate: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        repeat: 0,
        ease: [0.2, 0.65, 0.3, 0.9],
        delay: i * 0.1
      }
    }),
    hover: {
      scale: 1.1,
      y: -5,
      transition: {
        duration: 0.2,
        ease: "easeOut"
      }
    }
  }

  const waveAnimation = {
    initial: { 
      scaleX: 0.8,
      opacity: 0 
    },
    animate: {
      scaleX: 1,
      opacity: [0, 1, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  }

  const glowAnimation = {
    initial: { 
      opacity: 0,
      scale: 0.5
    },
    animate: {
      opacity: [0, 0.5, 0],
      scale: [0.5, 1.2, 0.5],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  }

  // Theme configuration
  const themeConfig = {
    modern: {
      primary: 'from-violet-500 to-fuchsia-500',
      secondary: 'from-indigo-500 to-purple-500',
      accent: 'from-fuchsia-500 to-pink-500',
      button: 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600',
      buttonSecondary: 'bg-slate-800/40 backdrop-blur-lg border border-slate-700/50 hover:bg-slate-800/60',
      card: 'bg-slate-900/50 backdrop-blur-xl border border-slate-700/50',
      text: 'text-slate-100',
      textSecondary: 'text-slate-300',
      glow: 'shadow-lg shadow-violet-500/20',
      toggle: 'bg-violet-500',
      chart: {
        primary: 'rgb(139, 92, 246)',
        background: 'rgba(139, 92, 246, 0.1)',
        grid: 'rgba(139, 92, 246, 0.1)'
      }
    },
    default: {
      primary: 'from-sky-500 to-blue-500',
      secondary: 'from-sky-400 to-blue-400',
      accent: 'from-sky-500 to-blue-500',
      button: 'bg-gradient-to-r from-sky-500 to-blue-500 hover:from-sky-600 hover:to-blue-600',
      buttonSecondary: 'bg-slate-800/40 backdrop-blur-lg border border-slate-700/50 hover:bg-slate-800/60',
      card: 'bg-slate-900/50 backdrop-blur-xl border border-slate-700/50',
      text: 'text-slate-100',
      textSecondary: 'text-slate-300',
      glow: 'shadow-lg shadow-sky-500/20',
      toggle: 'bg-sky-500',
      chart: {
        primary: 'rgb(14, 165, 233)',
        background: 'rgba(14, 165, 233, 0.1)',
        grid: 'rgba(14, 165, 233, 0.1)'
      }
    },
    ocean: {
      primary: 'from-cyan-500 to-blue-500',
      secondary: 'from-cyan-400 to-blue-400',
      accent: 'from-cyan-500 to-blue-500',
      button: 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600',
      buttonSecondary: 'bg-slate-800/40 backdrop-blur-lg border border-slate-700/50 hover:bg-slate-800/60',
      card: 'bg-slate-900/50 backdrop-blur-xl border border-slate-700/50',
      text: 'text-slate-100',
      textSecondary: 'text-slate-300',
      glow: 'shadow-lg shadow-cyan-500/20',
      toggle: 'bg-cyan-500',
      chart: {
        primary: 'rgb(6, 182, 212)',
        background: 'rgba(6, 182, 212, 0.1)',
        grid: 'rgba(6, 182, 212, 0.1)'
      }
    },
    sunset: {
      primary: 'from-orange-500 to-red-500',
      secondary: 'from-orange-400 to-red-400',
      accent: 'from-orange-500 to-red-500',
      button: 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600',
      buttonSecondary: 'bg-slate-800/40 backdrop-blur-lg border border-slate-700/50 hover:bg-slate-800/60',
      card: 'bg-slate-900/50 backdrop-blur-xl border border-slate-700/50',
      text: 'text-slate-100',
      textSecondary: 'text-slate-300',
      glow: 'shadow-lg shadow-orange-500/20',
      toggle: 'bg-orange-500',
      chart: {
        primary: 'rgb(249, 115, 22)',
        background: 'rgba(249, 115, 22, 0.1)',
        grid: 'rgba(249, 115, 22, 0.1)'
      }
    },
    forest: {
      primary: 'from-emerald-500 to-green-500',
      secondary: 'from-emerald-400 to-green-400',
      accent: 'from-emerald-500 to-green-500',
      button: 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600',
      buttonSecondary: 'bg-slate-800/40 backdrop-blur-lg border border-slate-700/50 hover:bg-slate-800/60',
      card: 'bg-slate-900/50 backdrop-blur-xl border border-slate-700/50',
      text: 'text-slate-100',
      textSecondary: 'text-slate-300',
      glow: 'shadow-lg shadow-emerald-500/20',
      toggle: 'bg-emerald-500',
      chart: {
        primary: 'rgb(16, 185, 129)',
        background: 'rgba(16, 185, 129, 0.1)',
        grid: 'rgba(16, 185, 129, 0.1)'
      }
    }
  }

  const activeTheme = themeConfig[options.theme]

  const navigate = useNavigate()

  useEffect(() => {
    fetchWeightData()
  }, [])

  const fetchWeightData = async () => {
    try {
      const { data, error } = await supabase
        .from('weight_entries')
        .select('*')
        .order('date', { ascending: true })

      if (error) throw error

      if (data && data.length > 0) {
        setWeightHistory(data)
        
        // Find yesterday's date in YYYY-MM-DD format
        const today = new Date()
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)
        const yesterdayStr = yesterday.toISOString().split('T')[0]
        
        // Find the entry for yesterday and day before yesterday
        const yesterdayEntry = data.find(entry => entry.date.startsWith(yesterdayStr))
        
        const twoDaysAgo = new Date(yesterday)
        twoDaysAgo.setDate(twoDaysAgo.getDate() - 1)
        const twoDaysAgoStr = twoDaysAgo.toISOString().split('T')[0]
        const dayBeforeYesterdayEntry = data.find(entry => entry.date.startsWith(twoDaysAgoStr))
        
        // Set yesterday's metrics for energy and cycling
        setYesterdayMetrics(yesterdayEntry || {})
        
        // Set current metrics from the latest entry
        const latestEntry = data[data.length - 1]
        const previousEntry = data[data.length - 2] || {}
        setCurrentMetrics(latestEntry)
        setCurrentWeight(latestEntry.weight)
        setPreviousWeight(data[data.length - 2]?.weight || null)
        
        // Set previous metrics for energy/cycling comparison
        setPreviousMetrics({
          ...previousEntry,
          active_energy: dayBeforeYesterdayEntry?.active_energy,
          cycling_distance: dayBeforeYesterdayEntry?.cycling_distance
        })
        
        setTimeout(() => setIsLoaded(true), 100)
      }
    } catch (error) {
      console.error('Error fetching weight data:', error)
    }
  }

  const getWeightDifference = () => {
    if (currentWeight === null || previousWeight === null) return null
    return (currentWeight - previousWeight).toFixed(1)
  }

  const getWeightAge = () => {
    if (!weightHistory.length) return null
    const lastEntry = weightHistory[weightHistory.length - 1]
    const lastDate = new Date(lastEntry.date)
    const now = new Date()
    // Add timezone offset and subtract 6 hours
    const offsetHours = -6
    const diffInMs = now.getTime() - lastDate.getTime() + 
      (now.getTimezoneOffset() * 60 * 1000) + 
      (offsetHours * 60 * 60 * 1000)
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
      return `${diffInMinutes}m ago`
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`
    } else {
      const diffInDays = Math.floor(diffInHours / 24)
      return `${diffInDays}d ago`
    }
  }

  const filteredWeightHistory = useMemo(() => {
    const now = new Date()
    const data = [...weightHistory]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-displayCount) // Limit the number of points

    switch (timeRange) {
      case '1w':
        return data.filter(entry => {
          const date = new Date(entry.date)
          return (now.getTime() - date.getTime()) <= 7 * 24 * 60 * 60 * 1000
        })
      case '2w':
        return data.filter(entry => {
          const date = new Date(entry.date)
          return (now.getTime() - date.getTime()) <= 14 * 24 * 60 * 60 * 1000
        })
      case '1m':
        return data.filter(entry => {
          const date = new Date(entry.date)
          return (now.getTime() - date.getTime()) <= 30 * 24 * 60 * 60 * 1000
        })
      case '1y':
        return data.filter(entry => {
          const date = new Date(entry.date)
          return (now.getTime() - date.getTime()) <= 365 * 24 * 60 * 60 * 1000
        })
      default:
        return data
    }
  }, [weightHistory, timeRange, displayCount])

  const getWeeklyAverages = (data: WeightEntry[]) => {
    const weeklyData: { [key: string]: number[] } = {}
    
    data.forEach(entry => {
      const date = new Date(entry.date)
      const day = date.getDay()
      const diff = date.getDate() - day + (day === 0 ? -6 : 1)
      const monday = new Date(date.setDate(diff))
      const weekKey = monday.toISOString().split('T')[0]
      const value = activeMetric === 'weight' 
        ? entry.weight 
        : activeMetric === 'energy'
        ? entry.active_energy || 0
        : entry.cycling_distance || 0
      
      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = []
      }
      weeklyData[weekKey].push(value)
    })

    return Object.entries(weeklyData).map(([weekKey, values]) => ({
      date: weekKey,
      value: activeMetric === 'cycling'
        ? values.reduce((sum, val) => sum + val, 0) // Sum for cycling
        : values.reduce((sum, val) => sum + val, 0) / values.length // Average for others
    }))
  }

  const getMonthlyAverages = (data: WeightEntry[]) => {
    const monthlyData: { [key: string]: number[] } = {}
    
    data.forEach(entry => {
      const date = new Date(entry.date)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      const value = activeMetric === 'weight' 
        ? entry.weight 
        : activeMetric === 'energy'
        ? entry.active_energy || 0
        : entry.cycling_distance || 0
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = []
      }
      monthlyData[monthKey].push(value)
    })

    return Object.entries(monthlyData).map(([monthKey, values]) => ({
      date: monthKey + '-01',
      value: activeMetric === 'cycling'
        ? values.reduce((sum, val) => sum + val, 0) // Sum for cycling
        : values.reduce((sum, val) => sum + val, 0) / values.length // Average for others
    }))
  }

  const processedData = useMemo(() => {
    switch (averageMode) {
      case 'weekly':
        return getWeeklyAverages(filteredWeightHistory)
      case 'monthly':
        return getMonthlyAverages(filteredWeightHistory)
      default:
        return filteredWeightHistory.map(entry => ({
          date: entry.date,
          value: activeMetric === 'weight' 
            ? entry.weight 
            : activeMetric === 'energy'
            ? entry.active_energy || 0
            : entry.cycling_distance || 0
        }))
    }
  }, [filteredWeightHistory, averageMode, activeMetric])

  const chartData = {
    labels: processedData.map(entry => {
      const date = new Date(entry.date)
      if (averageMode === 'monthly') {
        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
      } else if (averageMode === 'weekly') {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      } else {
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          ...(timeRange === '1y' && { year: '2-digit' })
        })
      }
    }),
    datasets: [
      {
        label: activeMetric === 'weight' 
          ? 'Weight' 
          : activeMetric === 'energy'
          ? 'Active Energy'
          : averageMode === 'monthly'
          ? 'Monthly Cycling Distance'
          : averageMode === 'weekly'
          ? 'Weekly Cycling Distance'
          : 'Cycling Distance',
        data: processedData.map(entry => entry.value),
        borderColor: !showDetailedGraph ? activeTheme.chart.primary : activeTheme.chart.primary,
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx
          const gradient = ctx.createLinearGradient(0, 0, 0, 300)
          gradient.addColorStop(0, !showDetailedGraph ? activeTheme.chart.background : activeTheme.chart.background)
          gradient.addColorStop(1, 'rgba(14, 165, 233, 0)')
          return gradient
        },
        tension: !showDetailedGraph ? 0.8 : 0.4,
        fill: true,
        pointRadius: showDetailedGraph ? 4 : 0,
        pointBackgroundColor: activeTheme.chart.primary,
        pointBorderColor: 'rgb(2, 6, 23)',
        pointBorderWidth: 2,
        borderWidth: !showDetailedGraph ? 1.5 : 2,
        cubicInterpolationMode: 'monotone' as const
      }
    ]
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: options.enableAnimations ? 750 : 0,
      easing: 'easeInOutQuart'
    },
    plugins: {
      legend: {
        display: false
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: showDetailedGraph
          },
          pinch: {
            enabled: showDetailedGraph
          },
          mode: 'x' as const
        },
        pan: {
          enabled: showDetailedGraph,
          mode: 'x' as const
        }
      },
      tooltip: {
        enabled: showDetailedGraph,
        backgroundColor: 'rgb(2, 6, 23)',
        titleColor: activeTheme.text.replace('text-', ''),
        bodyColor: activeTheme.text.replace('text-', ''),
        padding: 12,
        displayColors: false,
        callbacks: {
          label: (context: any) => {
            const unit = activeMetric === 'weight' 
              ? 'kg' 
              : activeMetric === 'energy'
              ? 'kcal'
              : 'km'
            return `${context.raw} ${unit}`
          }
        }
      }
    },
    scales: {
      x: {
        display: showDetailedGraph,
        grid: {
          display: showDetailedGraph,
          color: activeTheme.chart.grid
        },
        ticks: {
          color: 'rgb(255, 255, 255)',
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        display: showDetailedGraph,
        grid: {
          display: showDetailedGraph,
          color: activeTheme.chart.grid
        },
        ticks: {
          color: 'rgb(255, 255, 255)',
          callback: (value: number | string) => {
            const unit = activeMetric === 'weight' 
              ? 'kg' 
              : activeMetric === 'energy'
              ? 'kcal'
              : 'km'
            const decimals = activeMetric === 'weight' ? 1 : 0
            return `${Number(value).toFixed(decimals)} ${unit}`
          }
        },
        min: Math.min(...processedData.map(entry => entry.value)) - 
          (activeMetric === 'weight' ? 0.5 : activeMetric === 'energy' ? 50 : 0.5),
        max: Math.max(...processedData.map(entry => entry.value)) + 
          (activeMetric === 'weight' ? 0.5 : activeMetric === 'energy' ? 50 : 0.5)
      }
    }
  } as const

  const weightDiff = getWeightDifference()

  const getMetricDifference = (current: number | null | undefined, previous: number | null | undefined) => {
    if (current === null || current === undefined || previous === null || previous === undefined) return null
    return current - previous
  }

  // Initialize sound queue
  useEffect(() => {
    setSoundQueue(shuffleArray(CLICK_SOUNDS))
  }, [])

  const playNextSound = useCallback(() => {
    if (soundQueue.length === 0) {
      // If queue is empty, reshuffle all sounds
      setSoundQueue(shuffleArray(CLICK_SOUNDS))
      return
    }

    // Play next sound from queue
    const nextSound = soundQueue[0]
    const audio = new Audio(nextSound)
    audio.volume = 0.5
    audio.play().catch(e => console.log('Error playing sound:', e))

    // Remove played sound from queue
    setSoundQueue(prev => prev.slice(1))
  }, [soundQueue])

  const handleGlobalClick = useCallback((e: React.MouseEvent) => {
    if (!options.enableClickEffects) return
    
    // Play sound if enabled
    if (options.enableSounds) {
      playNextSound()
    }

    // Add click effect
    const newEffect = {
      id: Date.now(),
      x: e.clientX,
      y: e.clientY
    }
    setClickEffects(prev => [...prev, newEffect])

    // Remove effect after animation
    setTimeout(() => {
      setClickEffects(prev => prev.filter(effect => effect.id !== newEffect.id))
    }, 1000)
  }, [playNextSound, options.enableClickEffects, options.enableSounds])

  // Add function to calculate time ago
  const getTimeAgo = (timestamp: string) => {
    const now = new Date()
    const date = new Date(timestamp)
    const diffInMs = now.getTime() - date.getTime()
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`
    } else if (diffInMinutes < 1440) { // less than 24 hours
      const hours = Math.floor(diffInMinutes / 60)
      return `${hours}h ago`
    } else {
      const days = Math.floor(diffInMinutes / 1440)
      return `${days}d ago`
    }
  }

  // Add function to fetch last poop
  const fetchLastPoop = async () => {
    if (!options.showPoopCard) return
    
    try {
      const { data, error } = await supabase
        .from('poop_records')
        .select('timestamp')
        .order('timestamp', { ascending: false })
        .limit(1)

      if (error) throw error
      setLastPoop(data?.[0]?.timestamp || null)
    } catch (error) {
      console.error('Error fetching last poop:', error)
    }
  }

  // Add useEffect to fetch last poop when showPoopCard changes
  useEffect(() => {
    fetchLastPoop()
  }, [options.showPoopCard])

  // Add function to calculate energy for different periods
  const calculateEnergyForPeriod = (period: EnergyTimePeriod) => {
    const now = new Date()
    let startDate: Date
    let previousStartDate: Date

    switch (period) {
      case 'weekly':
        startDate = new Date(now)
        startDate.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1))
        startDate.setHours(0, 0, 0, 0)
        previousStartDate = new Date(startDate)
        previousStartDate.setDate(previousStartDate.getDate() - 7)
        break
      case 'monthly':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1)
        previousStartDate = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        break
      case 'yearly':
        startDate = new Date(now.getFullYear(), 0, 1)
        previousStartDate = new Date(now.getFullYear() - 1, 0, 1)
        break
    }

    const currentPeriodEnergy = weightHistory
      .filter(entry => new Date(entry.date) >= startDate)
      .reduce((sum, entry) => sum + (entry.active_energy || 0), 0)

    const previousPeriodEnergy = weightHistory
      .filter(entry => {
        const date = new Date(entry.date)
        return date >= previousStartDate && date < startDate
      })
      .reduce((sum, entry) => sum + (entry.active_energy || 0), 0)

    setPreviousMetrics(prev => ({
      ...prev,
      weekly_energy: Math.round(previousPeriodEnergy)
    }))

    return Math.round(currentPeriodEnergy)
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-sky-900/10">
      {/* Click Effects */}
      {clickEffects.map(effect => (
        <motion.div
          key={effect.id}
          className="fixed pointer-events-none z-50 select-none"
          initial={{ 
            opacity: 1, 
            scale: 0.5,
            x: effect.x - 50, // Center text around cursor
            y: effect.y - 10
          }}
          animate={{ 
            opacity: 0,
            scale: 1.5,
            y: effect.y - 100 // Float upward
          }}
          exit={{ opacity: 0 }}
          transition={{ 
            duration: 1,
            ease: "easeOut"
          }}
        >
          <span className="text-sky-300 font-bold text-lg bg-clip-text text-transparent bg-gradient-to-r from-sky-200 to-sky-400">
            Snackmeyers!
          </span>
        </motion.div>
      ))}

      {/* Background Graph */}
      {!showDetailedGraph && (
        <motion.div 
          className="absolute inset-x-0 top-0 h-[100vh] opacity-50 backdrop-blur-[1px]"
          initial={{ opacity: 1, scale: 1.1 }}
          animate={{ 
            opacity: isLoaded ? 0.9 : 0,
            scale: isLoaded ? 1 : 1.1,
          }}
          transition={{ 
            duration: 2,
            ease: "easeOut"
          }}
        >
          <motion.div 
            className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/80 to-transparent z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 1.2 }}
          />
          <motion.div 
            className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-950/50 to-slate-950 z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 1.2 }}
          />
          <motion.div 
            className="h-full w-full scale-150 translate-y-[-5%]"
            initial={{ y: "10%" }}
            animate={{ y: "-5%" }}
            transition={{ 
              duration: 2.5,
              ease: "easeInOut",
              delay: 0.3
            }}
          >
            <Line data={chartData} options={{
              ...chartOptions,
              animation: {
                duration: 2000,
                easing: 'easeInOutQuart'
              }
            }} />
          </motion.div>
        </motion.div>
      )}

      {/* Content */}
      <div className="relative z-10">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="flex items-center justify-between mb-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          >
            <div className="flex items-center gap-3">
              <h1 className="relative text-2xl font-bold py-2">
                {/* Background glow effect */}
                <motion.div
                  className="absolute inset-0 bg-sky-500/20 blur-2xl rounded-full"
                  variants={glowAnimation}
                  initial="initial"
                  animate="animate"
                />
                
                {/* Animated letters */}
                <div className="relative flex flex-wrap gap-2">
                  {words.map((word, index) => (
                    <motion.span
                      key={index}
                      custom={index}
                      variants={letterAnimationVariants}
                      initial="initial"
                      animate="animate"
                      whileHover="hover"
                      className="inline-block bg-clip-text text-transparent bg-gradient-to-r from-sky-200 via-slate-100 to-sky-200 cursor-default"
                    >
                      {word}
                    </motion.span>
                  ))}
                </div>

                {/* Multiple wave lines */}
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className={`absolute bottom-${i} left-0 right-0 h-[2px] bg-gradient-to-r from-sky-500/0 via-sky-500/${30 - i * 10} to-sky-500/0`}
                    variants={waveAnimation}
                    initial="initial"
                    animate="animate"
                    style={{
                      bottom: `${-4 - i * 2}px`,
                      animationDelay: `${i * 0.3}s`
                    }}
                  />
                ))}

                {/* Sparkles */}
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={`sparkle-${i}`}
                    className="absolute w-1 h-1 bg-sky-300 rounded-full"
                    animate={{
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0],
                      x: [0, Math.random() * 40 - 20],
                      y: [0, Math.random() * -30 - 10]
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: i * 0.4,
                      ease: "easeOut"
                    }}
                    style={{
                      left: `${20 + i * 30}%`,
                      top: "50%"
                    }}
                  />
                ))}
              </h1>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation()
                if (options.enableSounds) playNextSound()
                setShowDetailedGraph(!showDetailedGraph)
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200
                ${showDetailedGraph 
                  ? `bg-gradient-to-r ${activeTheme.primary} ${activeTheme.glow}` 
                  : activeTheme.buttonSecondary}
                ${activeTheme.text}`}
            >
              <ChartBarIcon className="h-5 w-5" />
              {showDetailedGraph ? 'Hide Graph' : 'Show Graph'}
            </button>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Current Weight Card */}
            <motion.div 
              className="stat-card md:col-span-2 lg:col-span-1 cursor-pointer"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ 
                duration: 0.8,
                ease: "easeOut",
                delay: 0.4
              }}
              onClick={(e) => {
                e.stopPropagation()
                setActiveMetric('weight')
                setShowDetailedGraph(true)
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="relative">
                <h2 className={`text-sm font-medium mb-4 ${activeTheme.textSecondary}`}>Current Weight</h2>
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  className="absolute top-0 right-0 text-sm text-slate-400 flex items-center gap-1.5"
                >
                  <motion.div
                    className="w-1 h-1 rounded-full bg-gradient-to-r from-sky-400 to-sky-300"
                    animate={{ 
                      scale: [1, 1.5, 1],
                      opacity: [0.5, 1, 0.5],
                      background: [
                        "linear-gradient(to right, rgb(56, 189, 248), rgb(125, 211, 252))",
                        "linear-gradient(to right, rgb(125, 211, 252), rgb(186, 230, 253))",
                        "linear-gradient(to right, rgb(56, 189, 248), rgb(125, 211, 252))"
                      ]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      times: [0, 0.5, 1]
                    }}
                  />
                  <motion.div
                    className="flex items-center overflow-hidden"
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: "auto", opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    <motion.span
                      className="whitespace-nowrap bg-clip-text text-transparent bg-gradient-to-r from-sky-200 to-sky-400"
                      animate={{ 
                        backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                      }}
                      transition={{
                        duration: 5,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                      style={{
                        backgroundSize: "200% 100%"
                      }}
                    >
                      {getWeightAge()}
                    </motion.span>
                  </motion.div>
                </motion.div>
                <AnimatePresence mode="wait">
                  {currentWeight !== null && (
                    <motion.div
                      key="weight"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.5 }}
                      className="flex flex-col"
                    >
                      <div className="flex items-end gap-3">
                        <motion.span
                          initial={{ opacity: 0, scale: 0.5 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.5, delay: 1 }}
                          className="text-7xl font-bold text-white leading-[0.9]"
                        >
                          {currentWeight.toFixed(1)}
                        </motion.span>
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5, delay: 1.2 }}
                          className="text-3xl text-slate-400 mb-1"
                        >
                          kg
                        </motion.span>
                      </div>
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 1.4 }}
                        className={`flex items-center gap-2 mt-2 ${weightDiff && (Number(weightDiff) < 0 ? 'text-emerald-400' : 'text-rose-400')}`}
                      >
                        {weightDiff && (Number(weightDiff) < 0 ? (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ 
                              type: "spring",
                              stiffness: 200,
                              damping: 10,
                              delay: 1.4
                            }}
                          >
                            <ArrowDownIcon className="h-5 w-5" />
                          </motion.div>
                        ) : (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ 
                              type: "spring",
                              stiffness: 200,
                              damping: 10,
                              delay: 1.4
                            }}
                          >
                            <ArrowUpIcon className="h-5 w-5" />
                          </motion.div>
                        ))}
                        <motion.span
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: 1.6 }}
                          className="text-base"
                        >
                          {weightDiff && Math.abs(Number(weightDiff))} kg
                        </motion.span>
                        <motion.span 
                          className="text-slate-500 text-sm"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5, delay: 1.8 }}
                        >
                          since last entry
                        </motion.span>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              title="Body Fat"
              value={currentMetrics.body_fat_percentage || null}
              unit="%"
              icon={HeartIcon}
              change={getMetricDifference(
                currentMetrics.body_fat_percentage,
                previousMetrics.body_fat_percentage
              )}
              invertColors={true}
              onPlaySound={playNextSound}
              theme={activeTheme}
            />
            <MetricCard
              title="BMI"
              value={currentMetrics.body_mass_index || null}
              unit=""
              icon={ScaleIcon}
              change={getMetricDifference(
                currentMetrics.body_mass_index,
                previousMetrics.body_mass_index
              )}
              invertColors={true}
              onPlaySound={playNextSound}
              theme={activeTheme}
            />
            <MetricCard
              title="Energy Yesterday"
              value={yesterdayMetrics.active_energy || null}
              unit="kcal"
              icon={FireIcon}
              change={getMetricDifference(
                yesterdayMetrics.active_energy,
                previousMetrics.active_energy
              )}
              onPlaySound={playNextSound}
              theme={activeTheme}
            />
            {/* Energy Period Card */}
            <MetricCard
              title={`Energy ${energyTimePeriod === 'weekly' ? 'This Week' : 
                     energyTimePeriod === 'monthly' ? 'This Month' : 'This Year'}`}
              value={useMemo(() => calculateEnergyForPeriod(energyTimePeriod), [weightHistory, energyTimePeriod])}
              unit="kcal"
              icon={FireIcon}
              change={getMetricDifference(
                currentMetrics.weekly_energy ? Math.round(currentMetrics.weekly_energy) : null,
                previousMetrics.weekly_energy ? Math.round(previousMetrics.weekly_energy) : null
              )}
              onPlaySound={playNextSound}
              onClick={() => {
                setEnergyTimePeriod(current => 
                  current === 'weekly' ? 'monthly' :
                  current === 'monthly' ? 'yearly' : 'weekly'
                )
                if (options.enableSounds) playNextSound()
              }}
              theme={activeTheme}
            />
            <MetricCard
              title="Cycling Yesterday"
              value={yesterdayMetrics.cycling_distance || null}
              unit="km"
              icon={BoltIcon}
              change={getMetricDifference(
                yesterdayMetrics.cycling_distance,
                previousMetrics.cycling_distance
              )}
              onPlaySound={playNextSound}
              onClick={() => {
                setActiveMetric('cycling')
                setShowDetailedGraph(true)
              }}
              theme={activeTheme}
            />
            {/* Last Poop Card */}
            {options.showPoopCard && (
              <MetricCard
                title="Last Poop"
                value={null}
                unit=""
                icon={() => <span className="text-2xl">💩</span>}
                onPlaySound={playNextSound}
                theme={activeTheme}
                customContent={
                  lastPoop ? (
                    <div className="mt-2 flex flex-col gap-1">
                      <div className={`text-2xl font-bold ${activeTheme.text}`}>
                        {getTimeAgo(lastPoop)}
                      </div>
                      <div className={`text-sm ${activeTheme.textSecondary}`}>
                        {weightHistory.length > 0 && lastPoop && (
                          new Date(lastPoop) > new Date(weightHistory[weightHistory.length - 1].date)
                            ? `${getTimeAgo(weightHistory[weightHistory.length - 1].date)} after last weight`
                            : `${getTimeAgo(weightHistory[weightHistory.length - 1].date)} before last weight`
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 text-base text-slate-400">No records yet</div>
                  )
                }
              />
            )}
          </div>

          {/* Detailed Graph Section */}
          <AnimatePresence>
            {showDetailedGraph && (
              <motion.div 
                className="mt-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.5 }}
              >
                <div className="dashboard-card">
                  <div className="flex flex-col space-y-4 sm:space-y-6">
                    {/* Mobile Layout */}
                    <div className="flex flex-col space-y-4 sm:hidden">
                      {/* Time Range Buttons */}
                      <div className="flex flex-col space-y-2">
                        <label className={`text-sm font-medium ${activeTheme.textSecondary}`}>Time Range</label>
                        <div className="grid grid-cols-5 gap-1.5">
                          {(['1w', '2w', '1m', '1y', 'all'] as const).map((range) => (
                            <button
                              key={range}
                              onClick={(e) => { 
                                e.stopPropagation()
                                if (options.enableSounds) playNextSound()
                                setTimeRange(range)
                              }}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                                ${timeRange === range 
                                  ? `bg-gradient-to-r ${activeTheme.primary} ${activeTheme.text} ${activeTheme.glow}` 
                                  : `${activeTheme.buttonSecondary}`}`}
                            >
                              {range}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Metric Selection */}
                      <div className="flex flex-col space-y-2">
                        <label className={`text-sm font-medium ${activeTheme.textSecondary}`}>Metric</label>
                        <div className="grid grid-cols-3 gap-1.5">
                          <button
                            onClick={(e) => { 
                              e.stopPropagation()
                              if (options.enableSounds) playNextSound()
                              setActiveMetric('weight')
                            }}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                              ${activeMetric === 'weight' 
                                ? `bg-gradient-to-r ${activeTheme.primary} ${activeTheme.text} ${activeTheme.glow}` 
                                : `${activeTheme.buttonSecondary}`}`}
                          >
                            Weight
                          </button>
                          <button
                            onClick={(e) => { 
                              e.stopPropagation()
                              if (options.enableSounds) playNextSound()
                              setActiveMetric('energy')
                            }}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                              ${activeMetric === 'energy' 
                                ? `bg-gradient-to-r ${activeTheme.primary} ${activeTheme.text} ${activeTheme.glow}` 
                                : `${activeTheme.buttonSecondary}`}`}
                          >
                            Energy
                          </button>
                          <button
                            onClick={(e) => { 
                              e.stopPropagation()
                              if (options.enableSounds) playNextSound()
                              setActiveMetric('cycling')
                            }}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                              ${activeMetric === 'cycling' 
                                ? `bg-gradient-to-r ${activeTheme.primary} ${activeTheme.text} ${activeTheme.glow}` 
                                : `${activeTheme.buttonSecondary}`}`}
                          >
                            Cycling
                          </button>
                        </div>
                      </div>

                      {/* Data View Mode */}
                      <div className="flex flex-col space-y-2">
                        <label className={`text-sm font-medium ${activeTheme.textSecondary}`}>Data View</label>
                        <div className="grid grid-cols-3 gap-1.5">
                          <button
                            onClick={(e) => { 
                              e.stopPropagation()
                              if (options.enableSounds) playNextSound()
                              setAverageMode('raw')
                            }}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                              ${averageMode === 'raw' 
                                ? `bg-gradient-to-r ${activeTheme.primary} ${activeTheme.text} ${activeTheme.glow}` 
                                : `${activeTheme.buttonSecondary}`}`}
                          >
                            Raw
                          </button>
                          <button
                            onClick={(e) => { 
                              e.stopPropagation()
                              if (options.enableSounds) playNextSound()
                              setAverageMode('weekly')
                            }}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                              ${averageMode === 'weekly' 
                                ? `bg-gradient-to-r ${activeTheme.primary} ${activeTheme.text} ${activeTheme.glow}` 
                                : `${activeTheme.buttonSecondary}`}`}
                          >
                            Weekly
                          </button>
                          <button
                            onClick={(e) => { 
                              e.stopPropagation()
                              if (options.enableSounds) playNextSound()
                              setAverageMode('monthly')
                            }}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                              ${averageMode === 'monthly' 
                                ? `bg-gradient-to-r ${activeTheme.primary} ${activeTheme.text} ${activeTheme.glow}` 
                                : `${activeTheme.buttonSecondary}`}`}
                          >
                            Monthly
                        </button>
                        </div>
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden sm:flex sm:items-center sm:gap-4">
                      {/* Time Range Buttons */}
                      <div className={`flex items-center gap-2 ${activeTheme.card} rounded-lg p-1`}>
                        {(['1w', '2w', '1m', '1y', 'all'] as const).map((range) => (
                          <button
                            key={range}
                            onClick={(e) => { 
                              e.stopPropagation()
                              if (options.enableSounds) playNextSound()
                              setTimeRange(range)
                            }}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200
                              ${timeRange === range 
                                ? `bg-gradient-to-r ${activeTheme.primary} bg-opacity-20 ${activeTheme.text}` 
                                : `${activeTheme.textSecondary} hover:text-slate-300`}`}
                            >
                              {range}
                            </button>
                          ))}
                      </div>

                      <div className={`flex items-center gap-2 ${activeTheme.card} rounded-lg p-1`}>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation()
                            if (options.enableSounds) playNextSound()
                            setActiveMetric('weight')
                          }}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200
                            ${activeMetric === 'weight' 
                              ? `bg-gradient-to-r ${activeTheme.primary} bg-opacity-20 ${activeTheme.text}` 
                              : `${activeTheme.textSecondary} hover:text-slate-300`}`}
                          >
                            Weight
                          </button>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation()
                            if (options.enableSounds) playNextSound()
                            setActiveMetric('energy')
                          }}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200
                            ${activeMetric === 'energy' 
                              ? `bg-gradient-to-r ${activeTheme.primary} bg-opacity-20 ${activeTheme.text}` 
                              : `${activeTheme.textSecondary} hover:text-slate-300`}`}
                          >
                            Energy
                        </button>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation()
                            if (options.enableSounds) playNextSound()
                            setActiveMetric('cycling')
                          }}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200
                            ${activeMetric === 'cycling' 
                              ? `bg-gradient-to-r ${activeTheme.primary} bg-opacity-20 ${activeTheme.text}` 
                              : `${activeTheme.textSecondary} hover:text-slate-300`}`}
                          >
                            Cycling
                        </button>
                      </div>

                      <div className={`flex items-center gap-2 ${activeTheme.card} rounded-lg p-1`}>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation()
                            if (options.enableSounds) playNextSound()
                            setAverageMode('raw')
                          }}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200
                            ${averageMode === 'raw' 
                              ? `bg-gradient-to-r ${activeTheme.primary} bg-opacity-20 ${activeTheme.text}` 
                              : `${activeTheme.textSecondary} hover:text-slate-300`}`}
                          >
                            Raw
                          </button>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation()
                            if (options.enableSounds) playNextSound()
                            setAverageMode('weekly')
                          }}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200
                            ${averageMode === 'weekly' 
                              ? `bg-gradient-to-r ${activeTheme.primary} bg-opacity-20 ${activeTheme.text}` 
                              : `${activeTheme.textSecondary} hover:text-slate-300`}`}
                          >
                            Weekly
                        </button>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation()
                            if (options.enableSounds) playNextSound()
                            setAverageMode('monthly')
                          }}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200
                            ${averageMode === 'monthly' 
                              ? `bg-gradient-to-r ${activeTheme.primary} bg-opacity-20 ${activeTheme.text}` 
                              : `${activeTheme.textSecondary} hover:text-slate-300`}`}
                          >
                            Monthly
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="graph-container h-[400px] mt-6">
                    <Line data={chartData} options={chartOptions} />
                  </div>
                  <div className="mt-4 flex justify-end">
                    <select
                      value={displayCount}
                      onChange={(e) => { 
                        e.stopPropagation()
                        if (options.enableSounds) playNextSound()
                        setDisplayCount(Number(e.target.value))
                      }}
                      className={`${activeTheme.card} ${activeTheme.text} rounded-lg px-3 py-1.5 text-sm
                        border border-slate-700 focus:border-${options.theme === 'modern' ? 'violet' : options.theme === 'ocean' ? 'cyan' : options.theme === 'sunset' ? 'orange' : options.theme === 'forest' ? 'emerald' : 'sky'}-500 focus:outline-none`}
                    >
                      <option value={25}>Last 25 points</option>
                      <option value={50}>Last 50 points</option>
                      <option value={100}>Last 100 points</option>
                      <option value={1000}>All points</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {showGame && (
          <WeightGame 
            weightHistory={weightHistory}
            onClose={() => setShowGame(false)}
          />
        )}
      </AnimatePresence>

      {/* Options Button */}
      <motion.button
        className="fixed bottom-6 right-6 p-3 rounded-full bg-slate-800/80 backdrop-blur-sm 
          text-slate-300 hover:bg-slate-700 transition-colors duration-200 z-50"
        onClick={(e) => {
          e.stopPropagation()
          setShowOptions(!showOptions)
        }}
        whileHover={{ rotate: 180 }}
        transition={{ duration: 0.3 }}
      >
        <ChartBarIcon className="h-6 w-6" />
      </motion.button>

      {/* Options Panel */}
      <AnimatePresence>
        {showOptions && (
          <motion.div
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 300 }}
            transition={{ type: "spring", damping: 20 }}
            className={`fixed top-0 right-0 h-full w-80 ${activeTheme.card} backdrop-blur-lg 
              border-l border-slate-700/50 p-6 shadow-xl z-40 overflow-y-auto`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-8">
              <div className="flex items-center justify-between">
                <motion.h2 
                  className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-100 to-slate-300"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  Options
                </motion.h2>
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (options.enableSounds) playNextSound()
                    setShowOptions(false)
                  }}
                  className="text-slate-400 hover:text-slate-300 transition-colors"
                  whileHover={{ rotate: 90 }}
                  transition={{ duration: 0.2 }}
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              </div>

              <div className="space-y-6">
                {/* Theme Switcher */}
                <motion.div 
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-1 h-6 rounded-full bg-gradient-to-b ${activeTheme.primary}`} />
                    <label className={`font-medium ${activeTheme.text}`}>Theme</label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { id: 'modern', label: 'Modern', gradient: 'from-violet-500 to-fuchsia-500', shadow: 'shadow-violet-500/20' },
                      { id: 'default', label: 'Default', gradient: 'from-sky-500 to-blue-500', shadow: 'shadow-sky-500/20' },
                      { id: 'ocean', label: 'Ocean', gradient: 'from-cyan-500 to-blue-500', shadow: 'shadow-cyan-500/20' },
                      { id: 'sunset', label: 'Sunset', gradient: 'from-orange-500 to-red-500', shadow: 'shadow-orange-500/20' },
                      { id: 'forest', label: 'Forest', gradient: 'from-emerald-500 to-green-500', shadow: 'shadow-emerald-500/20' }
                    ].map(theme => (
                      <motion.button
                        key={theme.id}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (options.enableSounds) playNextSound()
                          setOptions(prev => ({
                            ...prev,
                            theme: theme.id as ThemeType
                          }))
                        }}
                        className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200
                          ${options.theme === theme.id 
                            ? `bg-gradient-to-r ${theme.gradient} text-white shadow-lg ${theme.shadow} scale-105` 
                            : 'bg-slate-800/40 backdrop-blur-lg border border-slate-700/50 text-slate-300 hover:bg-slate-800/60'}`}
                        whileHover={{ scale: options.theme === theme.id ? 1.05 : 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {theme.label}
                      </motion.button>
                    ))}
                  </div>
                </motion.div>

                {/* Toggles Section */}
                <motion.div 
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-1 h-6 rounded-full bg-gradient-to-b ${activeTheme.primary}`} />
                    <label className={`font-medium ${activeTheme.text}`}>Features</label>
                  </div>
                  <div className="space-y-3 p-3 rounded-xl bg-slate-800/40 backdrop-blur-lg border border-slate-700/50">
                    {[
                      { id: 'enableSounds', label: 'Sound Effects', icon: '🔊' },
                      { id: 'enableAnimations', label: 'Animations', icon: '✨' },
                      { id: 'enableClickEffects', label: 'Click Effects', icon: '👆' },
                      { id: 'darkMode', label: 'Dark Mode', icon: '🌙' },
                      { id: 'showPoopCard', label: 'Show Poop Card', icon: '💩' }
                    ].map(feature => (
                      <div key={feature.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-800/40 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{feature.icon}</span>
                          <label className={activeTheme.textSecondary}>{feature.label}</label>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (options.enableSounds) playNextSound()
                            setOptions(prev => ({
                              ...prev,
                              [feature.id]: !prev[feature.id as keyof typeof prev]
                            }))
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full 
                            transition-all duration-300 focus:outline-none
                            ${options[feature.id as keyof typeof options] ? activeTheme.toggle : 'bg-slate-700'}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg
                              transition-all duration-300
                              ${options[feature.id as keyof typeof options] ? 'translate-x-6 scale-110' : 'translate-x-1'}`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      if (options.enableSounds) playNextSound()
                      setShowOptions(false)
                      setShowGame(true)
                    }}
                    className={`${activeTheme.button} ${activeTheme.text} ${activeTheme.glow} hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 shadow-lg shadow-${options.theme}-500/20`}
                  >
                    <ScaleIcon className="h-5 w-5" />
                    <span className="font-medium">Play Weight Flappy Game</span>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      if (options.enableSounds) playNextSound()
                      setOptions({
                        enableSounds: false,
                        enableAnimations: true,
                        enableClickEffects: true,
                        darkMode: true,
                        theme: 'modern' as ThemeType,
                        showPoopCard: false
                      })
                    }}
                    className={`${activeTheme.buttonSecondary} flex items-center justify-center gap-2`}
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span className="font-medium">Reset to Defaults</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default App